import type { ChatSummary, ModelSummary } from "../api/contracts";
import { chatsForModel } from "../state/workspace";

export function Sidebar({
  activeChatId,
  chats,
  creating,
  hasMoreChats,
  loadingMoreChats,
  navigationPending,
  models,
  onCreateChat,
  onLoadMoreChats,
  onSelectChat,
  onSelectModel,
  selectedModelId,
}: {
  activeChatId: string | null;
  chats: ChatSummary[];
  creating: boolean;
  hasMoreChats: boolean;
  loadingMoreChats: boolean;
  navigationPending: boolean;
  models: ModelSummary[];
  onCreateChat: () => Promise<void>;
  onLoadMoreChats: () => Promise<void>;
  onSelectChat: (chatId: string) => Promise<void>;
  onSelectModel: (modelId: string) => void;
  selectedModelId: string | null;
}) {
  const visibleChats = selectedModelId === null ? [] : chatsForModel(chats, selectedModelId);
  const selectedModel = models.find((model) => model.modelId === selectedModelId);

  return (
    <aside className="sidebar" aria-label="Dev Chat navigation">
      <div className="sidebar-brand">
        <div className="brand-mark brand-mark--small" aria-hidden="true">LB</div>
        <div>
          <span className="eyebrow">LoveBomber Platform</span>
          <strong>Dev Chat</strong>
        </div>
      </div>

      <div className="model-picker">
        <label htmlFor="dev-model">Dev model</label>
        <select
          id="dev-model"
          value={selectedModelId ?? ""}
          onChange={(event) => onSelectModel(event.target.value)}
          disabled={models.length === 0 || creating || navigationPending}
        >
          {models.length === 0 ? <option value="">No dev models</option> : null}
          {models.map((model) => (
            <option key={model.modelId} value={model.modelId}>{model.modelId}</option>
          ))}
        </select>
      </div>

      <div className="chat-list-heading">
        <span>Fans</span>
        <span>{visibleChats.length}</span>
      </div>

      <nav className="chat-list" aria-label="Chats for selected dev model">
        {visibleChats.length === 0 ? (
          <p className="empty-list">No chats yet.</p>
        ) : (
          visibleChats.map((chat) => (
            <button
              className={`chat-list-item${chat.chatId === activeChatId ? " chat-list-item--active" : ""}`}
              key={chat.chatId}
              data-chat-id={chat.chatId}
              data-fan-id={chat.fanId}
              data-model-id={chat.modelId}
              type="button"
              disabled={navigationPending || creating}
              onClick={() => void onSelectChat(chat.chatId)}
              aria-current={chat.chatId === activeChatId ? "page" : undefined}
            >
              <span className="chat-list-item__topline">
                <strong>{chat.fanId}</strong>
                <time dateTime={chat.lastItemAt ?? chat.updatedAt}>{formatCompactTime(chat.lastItemAt ?? chat.updatedAt)}</time>
              </span>
              <span className="chat-list-item__preview">{chat.lastItemPreview ?? "New fan"}</span>
            </button>
          ))
        )}
        {hasMoreChats ? (
          <button
            className="history-page-button history-page-button--chats"
            type="button"
            disabled={loadingMoreChats || navigationPending || creating}
            onClick={() => void onLoadMoreChats()}
          >
            {loadingMoreChats ? "Loading…" : "Load more chats"}
          </button>
        ) : null}
      </nav>

      <button
        className="new-chat-button"
        type="button"
        disabled={
          selectedModel === undefined ||
          selectedModel.readOnly ||
          creating ||
          navigationPending
        }
        title={
          selectedModel?.readOnly === true
            ? `Read-only: ${selectedModel.readinessReason}`
            : undefined
        }
        onClick={() => void onCreateChat()}
      >
        <span aria-hidden="true">＋</span>
        {creating ? "Creating…" : "New chat"}
      </button>
    </aside>
  );
}

function formatCompactTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
